'use client';

import { c as _c } from "react/compiler-runtime";
import './child-bio-image.scss';
import { useTranslation } from 'react-i18next';
import { HtmlBlock } from '@sola/ark-core/atoms/html/html.tsx';
import { ChildImageVariantEnum, getChildImageUrl } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { Image } from '../../atoms/image/image.tsx';
import { useSacImageAltText } from '../../utils/alt-text.ts';
import placeholder from './placeholder.png';
type ChildBioImageProps = {
  availability: ChildAvailabilityItem;
  details: ChildDetails;
  aspectRatio?: string;
  childImageVariant?: ChildImageVariantEnum;
  caption?: string;
  centerCaption?: boolean;
  width?: number;
  fullWidth?: boolean;
};
export const ChildBioImage = t0 => {
  const $ = _c(34);
  const {
    details,
    availability,
    aspectRatio: t1,
    childImageVariant: t2,
    caption,
    centerCaption: t3,
    width: t4,
    fullWidth: t5
  } = t0;
  const aspectRatio = t1 === undefined ? "3-4" : t1;
  const childImageVariant = t2 === undefined ? ChildImageVariantEnum.Full : t2;
  const centerCaption = t3 === undefined ? false : t3;
  const width = t4 === undefined ? 500 : t4;
  const fullWidth = t5 === undefined ? false : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["bio", "children", "geo"];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation(t6);
  const {
    imageUrl,
    name,
    hangul
  } = details;
  const {
    gender,
    isSpecialNeeds,
    isOrphan,
    isInHivAffectedArea,
    minDaysWaiting,
    icp: t7
  } = availability;
  const {
    country
  } = t7;
  let t8;
  if ($[1] !== childImageVariant || $[2] !== imageUrl || $[3] !== width) {
    t8 = getChildImageUrl(imageUrl, childImageVariant, width);
    $[1] = childImageVariant;
    $[2] = imageUrl;
    $[3] = width;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const childImage = t8;
  const titleAndAlt = useSacImageAltText(gender, country, hangul, minDaysWaiting, name.preferred, isOrphan, isSpecialNeeds, isInHivAffectedArea);
  let t9;
  if ($[5] !== caption || $[6] !== fullWidth || $[7] !== hangul || $[8] !== minDaysWaiting || $[9] !== name.preferred || $[10] !== t) {
    t9 = caption ?? t(!fullWidth ? "bio:waitingCaption" : "bio:waitingCaption_short", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[5] = caption;
    $[6] = fullWidth;
    $[7] = hangul;
    $[8] = minDaysWaiting;
    $[9] = name.preferred;
    $[10] = t;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const captionText = t9;
  let t10;
  if ($[12] !== fullWidth) {
    t10 = cn("ChildBioImage xl:pr-comfy-xl", {
      "ChildBioImage--fullWidth": fullWidth
    });
    $[12] = fullWidth;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = childImage || placeholder;
  let t12;
  if ($[14] !== t11 || $[15] !== titleAndAlt) {
    t12 = {
      url: t11,
      alt: titleAndAlt
    };
    $[14] = t11;
    $[15] = titleAndAlt;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== aspectRatio || $[18] !== childImage || $[19] !== t12) {
    t13 = <Image key={childImage} image={t12} lazyLoad="eager" aspectRatio={aspectRatio} className="ChildBioImage__Figure__img" />;
    $[17] = aspectRatio;
    $[18] = childImage;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = centerCaption || fullWidth;
  let t15;
  if ($[21] !== t14) {
    t15 = cn("ChildBioImage__Figure__figcaption label label-xl", {
      "text-center": t14
    });
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== captionText) {
    t16 = <HtmlBlock htmlString={captionText} />;
    $[23] = captionText;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t15 || $[26] !== t16) {
    t17 = <figcaption className={t15}>{t16}</figcaption>;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t13 || $[29] !== t17) {
    t18 = <figure className="ChildBioImage__Figure">{t13}{t17}</figure>;
    $[28] = t13;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t10 || $[32] !== t18) {
    t19 = <div className={t10}>{t18}</div>;
    $[31] = t10;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
};